import { useNetworkStore } from '@/stores/network.store';
import { axiosRequest as axiosInstance } from '@/common/api/instance';
import { AxiosError } from 'axios';
import { RequestConfig } from '@/types/api/response.types';

const RETRY_ERROR_CODES = new Set(['ECONNABORTED', 'ETIMEDOUT', 'ERR_NETWORK']);
const MAX_DELAY_IN_MS = 5000;
const MIN_DELAY_IN_MS = 500;
const BACKOFF_FACTOR = 1.5;

// Coordinated retry state
const queuedRequests = new Map<string, RequestConfig>(); // requestId -> config
let retryInProgress = false;
let leaderRequestId: string | null = null;

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * Handles retry logic for API requests with a coordinated pattern:
 * - Only the first failing request (leader) performs retries.
 * - All other failing requests during this period are queued.
 * - If the leader succeeds, queued requests are replayed once.
 *
 * @param error - The Axios error object.
 * @param retryCount - The maximum number of retries allowed.
 */
export const retryHandler = async (error: AxiosError, retryCount = 3): Promise<void> => {
  // Check if the error code is one of the retry-able error codes
  if (!RETRY_ERROR_CODES.has(error.code || '')) return;

  // Initialize the network store and request config
  const networkStore = useNetworkStore();
  const config = error.config as RequestConfig;
  if (!config || config.skipNetworkRetry) return;

  // Track this failed request in the queue
  const requestKey = config.requestId || `${config.method}:${config.url}`;
  if (!queuedRequests.has(requestKey)) {
    queuedRequests.set(requestKey, config);
  }

  // If a coordinated retry is already in progress, just surface the UI and exit
  if (retryInProgress) {
    networkStore.showRetryModal(true);
    networkStore.showRefreshButton(false);
    return;
  }

  // Become the leader and perform the retry attempts
  retryInProgress = true;
  leaderRequestId = requestKey;

  // Initialize the retries attempted if nullish
  config.retriesAttempted ??= 0;

  while (config.retriesAttempted < retryCount) {
    config.retriesAttempted += 1;

    networkStore.setRetryCount(config.retriesAttempted);
    networkStore.showRetryModal(true);
    networkStore.showRefreshButton(false);

    // Exponential backoff delay applied to the retry
    const delayDuration = Math.min(
      MAX_DELAY_IN_MS,
      MIN_DELAY_IN_MS * Math.pow(BACKOFF_FACTOR, config.retriesAttempted - 1),
    );

    await delay(delayDuration);

    try {
      // Try the leader request again
      await axiosInstance().request(config);

      // If it succeeds, close the modal and replay queued requests
      networkStore.showRetryModal(false);
      networkStore.showRefreshButton(false);

      // Collect queued requests except the leader
      const toReplay = [...queuedRequests.entries()]
        .filter(([key]) => key !== leaderRequestId)
        .map(([, cfg]) => cfg);

      // Clear the queue and reset the leader state before replay to avoid re-queuing
      queuedRequests.clear();
      retryInProgress = false;
      leaderRequestId = null;

      // Replay queued requests once; if any of these fail again, they will
      // go through the interceptor flow but should now pass if the network recovered
      for (const cfg of toReplay) {
        // request() rejects asynchronously, so a surrounding try/catch never sees the failure and it
        // surfaces as an unhandled rejection. The handler belongs on the promise itself.
        axiosInstance()
          .request(cfg)
          .catch(() => {
            // Ignore replay errors here; they will be handled by interceptors
          });
      }

      return; // Done handling after a successful leader retry
    } catch {
      // Continue the loop to attempt again until retryCount is reached
    }
  }

  // Retries exhausted for the leader
  networkStore.showRetryModal(true);
  networkStore.showRefreshButton(true);

  // Keep the queue intact; user may refresh or take action
  retryInProgress = false;
  leaderRequestId = null;
};
